import { formatBrlInteger } from "./money";

/**
 * Desired quantity and its split into packs (handoff §7.5, criteria 17.12 and 17.13).
 *
 * Two rules decide what a pack may be:
 *
 * 1. **it has to divide the total exactly.** A pack that does not divide would leave the last
 *    one incomplete, and since guests gift a closed pack, that remainder is something nobody
 *    can buy. This rule is the handoff's (criterion 17.13);
 * 2. **it has to hold an even number of pieces.** This one is the backend's: `bundleMultiple`
 *    carries `@IsDivisibleBy(2)` and `@Min(2)` (`update-gift-list-items.dto.ts`), confirmed as
 *    deliberate by the backend on 2026-10-08.
 *
 * The prototype only knew the first (`divisores(n)` in `Catalogo.dc.html` returns every
 * divisor, odd ones included), and this module followed it. So the dialog used to offer, say,
 * packs of 3 for a total of 9 — an arrangement the list could never hold, refused only at
 * write time by a toast. Now the impossible option is never drawn: see `packOptions`.
 */

/** Handoff §7.5: the prototype accepts 1 to 99 units. */
export const PACK_TOTAL_MIN = 1;
export const PACK_TOTAL_MAX = 99;

/** The smallest real pack. Below it there is no pack, which is `1` ("sem pacote"). */
export const PACK_SIZE_MIN = 2;

/**
 * Still reachable, and **not** by way of the dialog: a kit's `componentCount` becomes the
 * piece's `pack` (`mappers/catalog-product.ts`), so a three-piece kit added straight from the
 * grid — no dialog opened — carries an odd pack to the mapper, which refuses it.
 *
 * That path is a product question (a "conjunto de 3 peças" has no even arrangement to fall back
 * on) and is registered with the backend team. Until it is decided, this is what the
 * host is told, and it stays honest about the one thing they can act on.
 */
export const PACK_MUST_BE_EVEN_MESSAGE =
  "Os pacotes precisam ter um número par de unidades. Escolha outro tamanho de pacote.";

export type PackForm = {
  /** Desired number of physical pieces. */
  total: number;
  /** How many pieces go in each pack. `1` means no pack. */
  pack: number;
};

/**
 * Every exact divisor of `n`, ascending.
 *
 * Includes 1 and `n`, because "no pack" and "one pack holding everything" are both valid.
 */
export function divisors(n: number): number[] {
  if (!Number.isInteger(n) || n < 1) return [1];

  const out: number[] = [];
  for (let candidate = 1; candidate <= n; candidate += 1) {
    if (n % candidate === 0) out.push(candidate);
  }

  return out;
}

/**
 * The pack sizes actually offered for a total: **"sem pacote", plus every even divisor**.
 *
 * `1` leads and is unconditional — "no pack" is always available, and it is the answer for a
 * total with no even divisor at all. An odd total (9) has none, so it offers only "sem pacote";
 * the old grid offered 3 there and the save failed afterwards.
 *
 * Both rules of this module's docblock are applied here, in one place, so the grid cannot draw
 * an option the mapper would refuse.
 */
export function packOptions(total: number): number[] {
  const even = divisors(total).filter(
    (candidate) => candidate >= PACK_SIZE_MIN && candidate % 2 === 0,
  );

  return [1, ...even];
}

/** Whether a pack size is one the dialog would offer for this total. */
export function isOfferedPack(pack: number, total: number): boolean {
  return packOptions(total).includes(pack);
}

/**
 * How many pieces a piece wants the moment it enters the list: what it already asks for, or one —
 * one pack, when it comes in packs. A piece from the catalogue carries `total: 0` ("not on the
 * list"), and zero is not a quantity anyone wants nor one the backend takes (`@Min(1)`): the
 * dialog opens on it, and a piece added in bulk or by the curation's press goes in with it.
 */
export function entryTotal(piece: { total: number; pack: number }): number {
  return piece.total >= PACK_TOTAL_MIN ? piece.total : Math.max(PACK_TOTAL_MIN, piece.pack);
}

/**
 * Clamps the total to the accepted range.
 *
 * Only `NaN` needs a guard, since an empty or textual field is not a quantity and the
 * minimum is the honest guess. The clamp itself handles infinities.
 */
export function clampPackTotal(value: number): number {
  if (Number.isNaN(value)) return PACK_TOTAL_MIN;

  return Math.min(Math.max(Math.trunc(value), PACK_TOTAL_MIN), PACK_TOTAL_MAX);
}

/**
 * The form resolved from what the person typed, by **derivation** rather than state sync.
 *
 * A requested pack the total no longer offers falls back to 1 (criterion 17.13), instead of
 * keeping an impossible arrangement in state waiting for the next total. It is `packOptions`
 * that decides "offers", so typing 12 → picking 6 → typing 9 lands on "sem pacote", and so
 * does a piece reopened on a stored odd pack. The total arrives as **text** because an empty
 * field is a legitimate mid-typing draft, and clamping it to 1 right then makes any two-digit
 * number impossible to type.
 */
export function resolvePackForm(rawTotal: string, requestedPack: number): PackForm {
  const parsed = Number.parseInt(rawTotal, 10);
  const total = clampPackTotal(Number.isNaN(parsed) ? PACK_TOTAL_MIN : parsed);

  return { total, pack: isOfferedPack(requestedPack, total) ? requestedPack : 1 };
}

/** Label of one pack option, with the prototype's copy for the no-pack case. */
export function packOptionLabel(pack: number): string {
  return pack === 1 ? "sem pacote" : `${pack} unidades`;
}

/**
 * The sentence explaining the arrangement, in the prototype's two literal forms.
 */
export function packSummary(form: PackForm, unitPriceCents: number): string {
  if (form.pack === 1) {
    return form.total > 1
      ? `${form.total} unidades soltas. O convidado presenteia uma a uma.`
      : "1 unidade. O convidado presenteia a peça inteira.";
  }

  const packs = form.total / form.pack;
  const perPack = formatBrlInteger(unitPriceCents * form.pack);

  return `${packs} pacotes de ${form.pack}, a R$ ${perPack} cada. O convidado presenteia o pacote fechado.`;
}

/** The arrangement in short form, for the confirmation toast. */
export function packArrangementLabel(form: PackForm): string {
  if (form.pack > 1) return `${form.total / form.pack} pacotes de ${form.pack}`;

  return form.total > 1 ? `${form.total} unidades soltas` : "1 unidade";
}

/**
 * Dialog copy, which changes with membership: joining the list and reconfiguring an
 * arrangement are different gestures, and the label has to say which one this is.
 */
export function packDialogCopy(isInList: boolean) {
  return {
    eyebrow: isInList ? "Quantidade e pacotes" : "Adicionar à minha lista",
    submit: isInList ? "Salvar" : "Adicionar à minha lista",
    canRemove: isInList,
  };
}
